import { CollectionExportPolicy, LongRunningTaskStatus } from '@ikegps/core-api';
import { IkeToken } from '@ikegps/core-api';
import { action, makeObservable, observable } from 'mobx';
import { ImageButton } from '../../components/button/image.button';
import { MaterialIcon } from '../../components/icon/icons';
import { MenuComponent } from '../../components/menu/menu';
import { type MenuItem, MenuItemComponent } from '../../components/menu/menu.item';
import { IMithrilComponent } from '../../components/mithril.component';
import { ModalService } from '../../components/modal/modal.service';
import { NotificationService } from '../../components/notification.toast/notification.service';
import { Spinner } from '../../components/spinner/spinner';
import { DefaultTheme } from '../../components/theme';
import { T, Translate } from '../../i18n';
import type { MithrilVNode } from '../../mithril';
import { m } from '../../mithril';
import { Services } from '../../services/index';
import { CurrentState } from '../../state/page.state';
import { getLongRunningTaskManager, type LongRunningExport } from '../long.running.task.manager';
import { ReportTypeEnum } from '../report/ReportMaker';
import { viewExportError } from '../export.notification/export.notification';
import { ExportMenuController } from './export.menu.ctrl';

export class ExportMenu extends IMithrilComponent {
	menu: MenuComponent;

	/** Legacy mode: spinner state while an export is in progress */
	@observable private exporting = false;

	constructor() {
		super();
		makeObservable(this);
		this.menu = new MenuComponent({
			items: () => this.viewDownloadItems(),
		});
	}

	private get legacyMode(): boolean {
		return !Services.Token.has(IkeToken.FEATURE_NEW_EXPORT_UI);
	}

	get nodes(): MithrilVNode {
		if (this.exporting || !CurrentState.Tag.ready) {
			return Spinner.view();
		}

		const userProfile = Services.UserProfile.get(Services.User.current.objectId);

		if (!Services.UserProfile.canExportReports(userProfile)) {
			return ImageButton.view(MaterialIcon.FILE_DOWNLOAD, {
				title: T.EXPORT_MENU_TITLE_PERMISSION,
				disabled: true,
			});
		}

		if (CurrentState.CollectionEdit.isDirty) {
			return ImageButton.view(MaterialIcon.FILE_DOWNLOAD, {
				title: T.EXPORT_MENU_TITLE_DIRTY,
				disabled: true,
			});
		}

		if (CurrentState.Tag.collections.length > CollectionExportPolicy.MAX_LIMIT) {
			return ImageButton.view(MaterialIcon.FILE_DOWNLOAD, {
				title: T.EXPORT_MENU_TITLE_LIMIT,
				titleAttrs: { limit: CollectionExportPolicy.MAX_LIMIT },
				disabled: true,
			});
		}

		return m('div', [
			ImageButton.view(MaterialIcon.FILE_DOWNLOAD, {
				title: T.EXPORT_MENU_TITLE,
				onclick: this.menu.show,
				id: 'export-menu',
			}),
			this.menu.view(),
		]);
	}

	private viewDownloadItems(): MenuItem[] {
		const oneCollectionSelected = CurrentState.Tag.collections.length === 1;
		const tagsSelected = Boolean(CurrentState.Tag.tags.length);
		const singleJobSelected = CurrentState.Tag.tags.length === 1;
		const canReportShort = Services.Token.has(IkeToken.FEATURE_REPORT_SHORT);
		const hasReportCompositePhoto =
			Services.Token.has(IkeToken.FEATURE_REPORT_COMPOSITE_PHOTO) ||
			Services.Token.has(IkeToken.FEATURE_GROUP_IKE_ANALYZE);
		const hasReportCompositePhotoOnly = Services.Token.has(IkeToken.FEATURE_REPORT_COMPOSITE_ONLY);
		const hasReportPhoto =
			Services.Token.has(IkeToken.FEATURE_REPORT_PHOTO) || Services.Token.has(IkeToken.FEATURE_GROUP_IKE_ANALYZE);
		// This ensures that all collections have the same form revision which is required for importing excel
		const canExportImportableExcel =
			CurrentState.Tag.collections.every(
				(collectionSummery) => collectionSummery.formRevision === CurrentState.Tag.collections[0].formRevision
			) && Services.Token.hasAll([IkeToken.FEATURE_IMPORT_CSV, IkeToken.FEATURE_IMPORT_EXCEL]);

		const run = (func: () => Promise<string | void>) => {
			if (this.legacyMode) {
				return this.runExportLegacy(func);
			}
			return ExportMenuController.runExport(func);
		};

		const silent = this.legacyMode;

		return [
			{
				visible: tagsSelected,
				view: MenuItemComponent.view(Translate(T.EXPORT_KML)),
				action: () => run(() => ExportMenuController.exportKML({ silent })),
			},
			{
				visible: tagsSelected,
				view: MenuItemComponent.view(Translate(T.EXCEL)),
				action: () => run(() => ExportMenuController.exportExcel({ silent })),
			},
			{
				visible: canExportImportableExcel,
				view: MenuItemComponent.view(Translate(T.IMPORTABLE_EXCEL)),
				action: () => run(() => ExportMenuController.exportImportableExcel({ silent })),
			},
			{
				visible: tagsSelected,
				view: MenuItemComponent.view(Translate(T.EXPORT_JSON)),
				action: () => run(() => ExportMenuController.exportJSON({ silent })),
			},
			{
				visible: singleJobSelected && Services.Token.has(IkeToken.FEATURE_GROUP_IKE_ANALYZE),
				view: MenuItemComponent.view(Translate(T.EXPORT_SHAPEFILE)),
				action: () => run(() => ExportMenuController.exportShapefile({ silent })),
				id: 'export-shapefile',
			},
			{
				visible: tagsSelected,
				view: MenuItemComponent.view(Translate(T.EXPORT_REPORT)),
				action: () =>
					this.legacyMode
						? this.legacyExportPDF(ReportTypeEnum.ReportType, T.EXPORT_REPORT)
						: ExportMenuController.runExport(() => this.exportPDF(ReportTypeEnum.ReportType, T.EXPORT_REPORT)),
			},
			{
				visible: tagsSelected && canReportShort,
				view: MenuItemComponent.view(Translate(T.EXPORT_REPORT_SHORT)),
				action: () =>
					this.legacyMode
						? this.legacyExportPDF(ReportTypeEnum.ReportShortType, T.EXPORT_REPORT_SHORT)
						: ExportMenuController.runExport(() =>
								this.exportPDF(ReportTypeEnum.ReportShortType, T.EXPORT_REPORT_SHORT)
							),
			},
			{
				visible: tagsSelected && hasReportCompositePhoto,
				view: MenuItemComponent.view(Translate(T.EXPORT_REPORT_COMPOSITE_PHOTO)),
				action: () =>
					this.legacyMode
						? this.legacyExportPDF(ReportTypeEnum.ReportCompositePhotoType, T.EXPORT_REPORT_COMPOSITE_PHOTO)
						: ExportMenuController.runExport(() =>
								this.exportPDF(ReportTypeEnum.ReportCompositePhotoType, T.EXPORT_REPORT_COMPOSITE_PHOTO)
							),
			},
			{
				visible: tagsSelected && hasReportCompositePhotoOnly,
				view: MenuItemComponent.view(Translate(T.EXPORT_REPORT_COMPOSITE_PHOTO_ONLY)),
				action: () =>
					this.legacyMode
						? this.legacyExportPDF(ReportTypeEnum.ReportCompositePhotoOnlyType, T.EXPORT_REPORT_COMPOSITE_PHOTO_ONLY)
						: ExportMenuController.runExport(() =>
								this.exportPDF(ReportTypeEnum.ReportCompositePhotoOnlyType, T.EXPORT_REPORT_COMPOSITE_PHOTO_ONLY)
							),
			},
			{
				visible: tagsSelected && hasReportPhoto,
				view: MenuItemComponent.view(Translate(T.EXPORT_REPORT_PHOTO)),
				action: () =>
					this.legacyMode
						? this.legacyExportPDF(ReportTypeEnum.ReportPhotoType, T.EXPORT_REPORT_PHOTO)
						: ExportMenuController.runExport(() =>
								this.exportPDF(ReportTypeEnum.ReportPhotoType, T.EXPORT_REPORT_PHOTO)
							),
			},
			{
				visible: tagsSelected && ExportMenuController.hasOCalcForm(CurrentState.Tag.collections),
				view: MenuItemComponent.view(Translate(T.EXPORT_O_CALC)),
				action: () => run(() => ExportMenuController.exportOCalc({ silent })),
			},
			{
				visible: tagsSelected && ExportMenuController.hasSPIDACalcForm(CurrentState.Tag.collections),
				view: MenuItemComponent.view(Translate(T.EXPORT_SPIDA_CALC)),
				action: () => run(() => ExportMenuController.exportSPIDACalc(false, { silent })),
			},
			{
				visible: tagsSelected && ExportMenuController.hasSPIDACalcForm(CurrentState.Tag.collections),
				view: MenuItemComponent.view(Translate(T.EXPORT_SPIDA_CALC_NO_PHOTO)),
				action: () => run(() => ExportMenuController.exportSPIDACalc(true, { silent })),
			},
			{
				visible: tagsSelected && ExportMenuController.hasPoleforeman(CurrentState.Tag.collections),
				view: MenuItemComponent.view(Translate(T.EXPORT_POLEFOREMAN)),
				action: () => run(() => ExportMenuController.exportPoleforeman({ silent })),
			},
			{
				visible: tagsSelected && ExportMenuController.hasIkePoleForeman(CurrentState.Tag.collections),
				view: MenuItemComponent.view(Translate(T.EXPORT_IKE_POLEFOREMAN)),
				action: () => run(() => ExportMenuController.exportIkePoleForeman({ silent })),
				id: 'export-ipf',
			},
			{
				visible: oneCollectionSelected,
				view: MenuItemComponent.view(Translate(T.EXPORT_IMAGE_ZIP)),
				action: () => run(() => ExportMenuController.exportImageZip({ silent })),
			},
			{
				visible: tagsSelected && ExportMenuController.hasDDS(CurrentState.Tag.collections),
				view: MenuItemComponent.view(Translate(T.EXPORT_DDS)),
				action: () => run(() => ExportMenuController.exportDDS({ silent })),
			},
		];
	}

	// ── New export UI: PDF via download manager ──

	private async exportPDF(type: ReportTypeEnum, reportNameTranslationKey: string): Promise<void> {
		const taskManager = getLongRunningTaskManager();
		const jobName = ExportMenuController.getExportLabel();
		const exportTypeLabel = Translate(reportNameTranslationKey);
		const taskId = taskManager.service.addFastExportTask(exportTypeLabel, jobName);

		try {
			const result = await Services.Department.rest.Department.User.isLicensed(null);
			if (!result.results) {
				Services.License.hasNoLicense = true;
				taskManager.service.failFastExportTask(taskId, undefined, 'License check failed');
				return;
			}

			const pdf = await ExportMenuController.exportReport(type);
			const fileName = `${Translate(reportNameTranslationKey)} ${new Date().toDateString()}.pdf`;

			const blob = await new Promise<Blob>((resolve, reject) => {
				try {
					pdf.getBlob((b: Blob) => resolve(b));
				} catch (err) {
					reject(err);
				}
			});
			const url = URL.createObjectURL(blob);
			taskManager.service.completeFastExportTask(taskId, url, fileName);
		} catch (_err) {
			taskManager.service.failFastExportTask(taskId, undefined, Translate(T.ERROR_GENERAL));
		}
	}

	// ── Legacy export mode (no new-export-ui token) ──

	/**
	 * Legacy export orchestration: blocks the UI with a spinner on the export button,
	 * waits for the task to complete, then auto-downloads or shows notification modal.
	 */
	@action.bound
	private async runExportLegacy(func: () => Promise<string | void>): Promise<void> {
		this.exporting = true;
		const taskManager = getLongRunningTaskManager();
		taskManager.state.pendingExports++;
		try {
			const taskId = await ExportMenuController.runExport(func);
			if (!taskId) return;

			const task = await taskManager.service.awaitTaskCompletion(taskId as string);

			// Mark as fully handled since legacy mode auto-handles everything
			const taskUpdate: Partial<LongRunningExport> = { seen: true, actioned: true, notified: true };

			taskManager.state.updateTask(task.taskId, taskUpdate);

			if (task.status === LongRunningTaskStatus.COMPLETED) {
				taskManager.service.downloadTask(task.taskId);
			} else if (task.status === LongRunningTaskStatus.FAILED) {
				await ModalService.open({
					title: Translate(T.NOTIFICATION_ERROR_TITLE),
					content: viewExportError(task.error || Translate(T.ERROR_EXPORT)),
				});
			} else {
				// COMPLETED_WARNINGS or COMPLETED_ERRORS — show notification modal
				await taskManager.service.showNotificationModal(task.taskId);
			}
		} catch (_) {
			NotificationService.error({ key: T.ERROR_GENERAL });
		} finally {
			taskManager.state.pendingExports--;
			this.exporting = false;
		}
	}

	/**
	 * Legacy PDF export: shows a modal with spinner, auto-downloads on completion.
	 * Matches the old production behavior from before the download manager UI.
	 */
	private async legacyExportPDF(type: ReportTypeEnum, reportNameTranslationKey: string): Promise<void> {
		const taskManager = getLongRunningTaskManager();
		taskManager.state.pendingExports++;

		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		ModalService.open({
			title: Translate(T.EXPORTING_PDF_TITLE),
			content: m('div', {}, [
				m('div', { style: { marginBottom: DefaultTheme.space(2) } }, Translate(T.EXPORTING_PDF_CONTENT)),
				Spinner.view(),
			]),
			hasCancel: false,
		});

		try {
			const result = await Services.Department.rest.Department.User.isLicensed(null);
			if (result.results) {
				(await ExportMenuController.exportReport(type)).download(
					`${Translate(reportNameTranslationKey)} ${new Date().toDateString()}`,
					() => {
						taskManager.state.pendingExports--;
						ModalService.close();
					}
				);
			} else {
				Services.License.hasNoLicense = true;
				taskManager.state.pendingExports--;
				ModalService.close();
			}
		} catch (_err) {
			taskManager.state.pendingExports--;
			ModalService.close();
			NotificationService.error({ key: T.ERROR_GENERAL });
		}
	}
}
